import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingDown, Eye, FileCheck, TrendingUp, ArrowUpRight } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { Benefit } from '../types';

gsap.registerPlugin(ScrollTrigger);

const benefits: Benefit[] = [
  {
    icon: TrendingDown,
    title: 'Reduce Costs by 15-20%',
    description: 'Automate billing, eliminate manual errors, and optimize routes to cut operational expenses significantly.',
    color: 'green',
    features: ['Automated invoicing', 'Dynamic pricing rules', 'Fuel monitoring'],
    href: '/pricing',
  },
  {
    icon: Eye,
    title: 'Real-Time Visibility',
    description: 'Know where every vehicle is, anytime. GPS tracking with live updates and route optimization.',
    color: 'blue',
    features: ['Live GPS tracking', 'Geofencing alerts', 'Route deviations'],
    href: '/employee-transport',
  },
  {
    icon: FileCheck,
    title: 'Stay Compliant',
    description: 'Never miss a document deadline again. Automated reminders for licenses, insurance, and certificates.',
    color: 'purple',
    features: ['Auto reminders', 'Digital documents', 'Compliance reports'],
    href: '/scmt',
  },
  {
    icon: TrendingUp,
    title: 'Scale Without Chaos',
    description: 'From 10 vehicles to 10,000+, the same powerful platform grows with your business.',
    color: 'orange',
    features: ['Multi-location support', 'Role-based access', 'API integrations'],
    href: '/trucks',
  },
];

const getColorClasses = (color: string) => {
  const colors: Record<string, { bg: string; text: string; gradient: string }> = {
    green: { bg: 'bg-green-500', text: 'text-green-600', gradient: 'from-green-500/10 to-green-600/5' },
    blue: { bg: 'bg-blue-500', text: 'text-blue-600', gradient: 'from-blue-500/10 to-blue-600/5' },
    purple: { bg: 'bg-purple-500', text: 'text-purple-600', gradient: 'from-purple-500/10 to-purple-600/5' },
    orange: { bg: 'bg-orange-500', text: 'text-orange-600', gradient: 'from-orange-500/10 to-orange-600/5' },
  };
  return colors[color] || colors.green;
};

const BenefitCard: React.FC<{ benefit: Benefit; index: number }> = ({ benefit, index }) => {
  const cardRef = useRef<HTMLAnchorElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const Icon = benefit.icon;
  const colors = getColorClasses(benefit.color);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const trigger = ScrollTrigger.create({
      trigger: card,
      start: 'top 85%',
      onEnter: () => setIsVisible(true),
      once: true
    });

    return () => trigger.kill();
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;

    gsap.to(card, {
      rotateX: rotateX,
      rotateY: rotateY,
      duration: 0.3,
      ease: 'power2.out',
      transformPerspective: 1000
    });
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    if (!card) return;

    gsap.to(card, {
      rotateX: 0,
      rotateY: 0,
      duration: 0.5,
      ease: 'elastic.out(1, 0.5)'
    });
  };

  return (
    <Link
      to={benefit.href}
      ref={cardRef}
      className={`
        relative group cursor-pointer block
        transition-all duration-700 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}
      `}
      style={{ 
        transitionDelay: `${index * 100}ms`,
        transformStyle: 'preserve-3d'
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className={`
        relative h-full p-8 rounded-3xl
        bg-gradient-to-br ${colors.gradient}
        border border-gray-100
        hover:border-gray-200 hover:shadow-xl
        transition-all duration-500
        overflow-hidden
      `}>
        {/* Hover glow effect */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Icon */}
          <div className={`
            w-14 h-14 rounded-2xl ${colors.bg}
            flex items-center justify-center
            mb-6 shadow-sm
            group-hover:scale-110 group-hover:shadow-md
            transition-all duration-500
          `}>
            <Icon className="w-7 h-7 text-white" />
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-navy-950 font-heading mb-3 leading-tight">
            {benefit.title}
          </h3>

          {/* Description */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            {benefit.description}
          </p>

          {/* Features */}
          <ul className="space-y-3 mb-6">
            {benefit.features.map((feature, idx) => (
              <li key={idx} className="flex items-center gap-3 text-sm text-gray-600">
                <div className={`
                  w-2 h-2 rounded-full ${colors.bg}
                  group-hover:scale-125 transition-transform duration-300
                `} />
                {feature}
              </li>
            ))}
          </ul>

          {/* CTA */}
          <div className={`flex items-center gap-2 ${colors.text} font-semibold group-hover:gap-3 transition-all duration-300`}>
            <span>Learn more</span>
            <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
          </div>
        </div>
      </div>
    </Link>
  );
};

const Benefits: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const trigger = ScrollTrigger.create({
      trigger: section,
      start: 'top 80%',
      onEnter: () => setHeaderVisible(true),
      once: true
    });

    return () => trigger.kill();
  }, []);

  return (
    <section ref={sectionRef} id="benefits" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary-50 rounded-full blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent-orange/10 rounded-full blur-3xl opacity-30 translate-x-1/2 translate-y-1/2" />

      <div className="container-main relative z-10">
        {/* Section Header */}
        <div className={`
          text-center max-w-3xl mx-auto mb-16
          transition-all duration-700 ease-out
          ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        `}>
          <h2 className="text-4xl lg:text-5xl font-bold text-navy-950 mb-6 font-heading leading-tight">
            All-in-One Fleet Management,{' '}
            <span className="text-primary-600">Simplified</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Say goodbye to spreadsheets and phone calls. Our platform seamlessly manages your entire fleet operation—from scheduling and billing to compliance—all in one place.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {benefits.map((benefit, index) => (
            <BenefitCard key={index} benefit={benefit} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Benefits;
